import { useEffect, useState } from "react";
import { Popover } from "react-tiny-popover";
import SearchInput from "../../component/searchInput/SearchInput";
import {
  CreateButton,
  InviteButton,
  SecondaryButton,
} from "../buttonComponent/ButtonComponent";
import Dropdown from "../dropdown/Dropdown";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useIsMobile } from "../../config/utilities/useBreakpoint";

const AUDIT_DATE_PICKER_SX = {
  "& .MuiOutlinedInput-root": {
    height: "2.5rem",
    borderRadius: "0.5rem",
    boxShadow: "0 0.063rem 0.125rem rgba(16, 24, 40, 0.05)",
    fontFamily: "Inter",
    fontSize: "0.875rem",
    "& fieldset": {
      border: "0.063rem solid #D0D5DD",
    },
    "&:hover fieldset": {
      borderColor: "#D0D5DD",
    },
    "&.Mui-focused fieldset": {
      border: "0.125rem solid #4A90E2",
      boxShadow: "0 0 0.3rem rgba(74, 144, 226, 0.45)",
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#A0AEC0",
    opacity: 1,
  },
};

export default function TableControls({
  searchTerm,
  handleSearchChange,
  searchText = "Search",
  showSearchBar = true,
  showFilter = true,
  onClick,
  module,
  showTableAction,
  filters = [],
  selectedFilters = {},
  setSelectedFilters,
  onBulkImport,
  onUpdateEmailStatus,
}) {
  const isMobileViewport = useIsMobile();
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [showActionsMobile, setShowActionsMobile] = useState(false);

  useEffect(() => {
    if (!isMobileViewport) {
      setShowFiltersMobile(false);
      setShowActionsMobile(false);
    }
  }, [isMobileViewport]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setSelectedFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleDateFilterChange = (name, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetFilters = () => {
    const cleared = {};
    for (const key of Object.keys(selectedFilters)) {
      cleared[key] = "";
    }
    setSelectedFilters(cleared);
  };

  const hasFilters = Array.isArray(filters) && filters.length > 0;
  // Audit uses a popover filter menu — not the generic inline filter row.
  const showFilterSection =
    showFilter &&
    hasFilters &&
    (module === "notifications" ||
      module === "stops" ||
      module === "trips" ||
      module === "vehicles" ||
      module === "drivers");

  const isAuditModule = module === "audit";
  const [auditFiltersPopoverOpen, setAuditFiltersPopoverOpen] = useState(false);

  const auditFiltersActive = Object.values(selectedFilters).some((v) => v !== "");
  const auditActiveFilterCount = Object.values(selectedFilters).filter(
    (v) => v !== "",
  ).length;

  const setAuditFilterValue = (name, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const hasActions = Boolean(showTableAction);
  const isMultiActionModule = module === "family";

  const renderActionButtons = () => (
    <>
      {module === "family" && (
        <>
          {onUpdateEmailStatus && (
            <SecondaryButton
              width="auto"
              label="Bulk Send Emails"
              imageIcon={false}
              onClickFunction={onUpdateEmailStatus}
              fontSize="0.875rem"
            />
          )}
          {onBulkImport && (
            <SecondaryButton
              width="auto"
              label="Bulk Import"
              imageIcon={false}
              onClickFunction={onBulkImport}
              fontSize="0.875rem"
            />
          )}
          <CreateButton
            width="auto"
            label="Create Family"
            onClickFunction={onClick}
          />
        </>
      )}
      {module === "admin" && <InviteButton width="auto" onClickFunction={onClick} />}
      {module === "parent" && (
        <CreateButton width="auto" label="Add Parent" onClickFunction={onClick} />
      )}
      {module === "child" && (
        <CreateButton width="auto" label="Add Child" onClickFunction={onClick} />
      )}
      {module === "notifications" && (
        <CreateButton
          width="auto"
          label="Create Campaign"
          onClickFunction={onClick}
        />
      )}
      {module === "stops" && (
        <CreateButton width="auto" label="Create Stop" onClickFunction={onClick} />
      )}
      {module === "associatedStudent" && (
        <CreateButton width="auto" label="Add Student" onClickFunction={onClick} />
      )}
      {module === "trips" && (
        <CreateButton width="auto" label="Create Trip" onClickFunction={onClick} />
      )}
      {module === "stopsArray" && (
        <CreateButton width="auto" label="Add Stop" onClickFunction={onClick} />
      )}
      {module === "vehicles" && (
        <CreateButton
          width="auto"
          label="Create Vehicle"
          onClickFunction={onClick}
        />
      )}
      {module === "drivers" && (
        <CreateButton width="auto" label="Create Driver" onClickFunction={onClick} />
      )}
      {module === "serviceAnnouncement" && (
        <CreateButton
          width="auto"
          label="Create Service Announcement"
          onClickFunction={onClick}
        />
      )}
      {module === "busPolicy" && (
        <CreateButton
          width="auto"
          label="Create Bus Policy"
          onClickFunction={onClick}
        />
      )}
      {module === "generalPolicy" && (
        <CreateButton
          width="auto"
          label="Create General Policy"
          onClickFunction={onClick}
        />
      )}
    </>
  );

  const auditFilterSectionLabels = {
    actionType: "Action",
    module: "Module",
    role: "Role",
  };

  const auditFilterAllLabels = {
    actionType: "All actions",
    module: "All modules",
    role: "All roles",
  };

  const auditFilterPopoverContent = (
    <div className="table-audit-filters-popover">
      <div className="table-audit-filters-popover-header">
        <span className="table-audit-filters-popover-title">Filters</span>
        {auditFiltersActive && (
          <button
            type="button"
            className="table-audit-filters-popover-clear"
            onClick={resetFilters}
          >
            Clear all
          </button>
        )}
      </div>

      {filters.map((filter, index) => (
        <div
          key={filter.name}
          className={`table-audit-filters-popover-section${
            index > 0 ? " table-audit-filters-popover-section--spaced" : ""
          }`}
        >
          <span className="table-audit-filters-popover-section-title">
            {auditFilterSectionLabels[filter.name] || filter.placeholder}
          </span>
          <div
            className="table-audit-filter-pills"
            role="group"
            aria-label={auditFilterSectionLabels[filter.name] || filter.name}
          >
            <button
              type="button"
              className={`table-audit-filter-pill table-audit-filter-pill-all${
                !selectedFilters[filter.name] ? " is-active" : ""
              }`}
              onClick={() => setAuditFilterValue(filter.name, "")}
            >
              {auditFilterAllLabels[filter.name] || "All"}
            </button>
            {filter.options.map((option) => {
              const isActive = selectedFilters[filter.name] === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`table-audit-filter-pill${
                    isActive ? " is-active" : ""
                  }`}
                  onClick={() => setAuditFilterValue(filter.name, option.value)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div className="table-audit-filters-popover-section table-audit-filters-popover-section--spaced">
        <span className="table-audit-filters-popover-section-title">
          Date range
        </span>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="table-audit-filters-date-row">
            <div className="table-audit-filters-date-field">
              <DatePicker
                value={
                  selectedFilters.startDate
                    ? dayjs(selectedFilters.startDate)
                    : null
                }
                onChange={(newValue) =>
                  handleDateFilterChange(
                    "startDate",
                    newValue && dayjs(newValue).isValid()
                      ? dayjs(newValue).format("YYYY-MM-DD")
                      : "",
                  )
                }
                maxDate={
                  selectedFilters.endDate
                    ? dayjs(selectedFilters.endDate)
                    : undefined
                }
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    placeholder: "Start date",
                    fullWidth: true,
                    variant: "outlined",
                    inputProps: { placeholder: "Start date" },
                    sx: AUDIT_DATE_PICKER_SX,
                  },
                }}
              />
            </div>
            <div className="table-audit-filters-date-field">
              <DatePicker
                value={
                  selectedFilters.endDate
                    ? dayjs(selectedFilters.endDate)
                    : null
                }
                onChange={(newValue) =>
                  handleDateFilterChange(
                    "endDate",
                    newValue && dayjs(newValue).isValid()
                      ? dayjs(newValue).format("YYYY-MM-DD")
                      : "",
                  )
                }
                minDate={
                  selectedFilters.startDate
                    ? dayjs(selectedFilters.startDate)
                    : undefined
                }
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    placeholder: "End date",
                    fullWidth: true,
                    variant: "outlined",
                    inputProps: { placeholder: "End date" },
                    sx: AUDIT_DATE_PICKER_SX,
                  },
                }}
              />
            </div>
          </div>
        </LocalizationProvider>
      </div>
    </div>
  );

  return (
    <div className="table-controls">
      <div className="table-search">
        {showSearchBar && (
          <div
            className={`table-search-searchbar ${
              isAuditModule ? "table-audit-toolbar-item" : ""
            }`}
          >
            <SearchInput
              containerWidth="min(24rem, 100%)"
              containerHeight="2.8rem"
              text={searchText}
              value={searchTerm}
              onChangeText={handleSearchChange}
            />
          </div>
        )}

        {showFilter && hasFilters && isAuditModule && (
          <div className="table-search-filter-audit-popover table-audit-toolbar-item">
            <Popover
              isOpen={auditFiltersPopoverOpen}
              positions={["bottom", "left", "top", "right"]}
              align="start"
              padding={8}
              onClickOutside={() => setAuditFiltersPopoverOpen(false)}
              containerStyle={{ zIndex: 1000 }}
              content={auditFilterPopoverContent}
            >
              <button
                type="button"
                className={`table-audit-filters-trigger${
                  auditFiltersActive ? " has-active-filters" : ""
                }${auditFiltersPopoverOpen ? " is-open" : ""}`}
                onClick={() => setAuditFiltersPopoverOpen((open) => !open)}
                aria-expanded={auditFiltersPopoverOpen}
                aria-haspopup="dialog"
                aria-label={
                  auditFiltersActive
                    ? `Filters: ${auditActiveFilterCount} active. Open menu.`
                    : "Filters. Open menu."
                }
              >
                <span className="table-audit-filters-trigger-label">
                  Filters
                </span>
                {auditFiltersActive && (
                  <span
                    className="table-audit-filters-badge"
                    aria-hidden="true"
                  >
                    {auditActiveFilterCount}
                  </span>
                )}
              </button>
            </Popover>
          </div>
        )}

        {showFilterSection && (!isMobileViewport || showFiltersMobile) && (
          <div className="table-search-filter">
              {filters.map((f) => (
                <div key={f.name} className="table-filter-item">
                  <Dropdown
                    options={f.options}
                    placeholder={f.placeholder}
                    onChangeFunction={handleFilterChange}
                    name={f.name}
                    input={selectedFilters}
                    width="100%"
                  />
                </div>
              ))}
              <div className="table-filter-clear">
                <SecondaryButton
                  label="Clear Filters"
                  onClickFunction={resetFilters}
                  disable={Object.values(selectedFilters).every(
                    (v) => v === "",
                  )}
                />
              </div>
            </div>
          )}

      </div>

      <div
        className={`table-create-button ${
          isMobileViewport && !showActionsMobile ? "mobile-collapsed" : ""
        }`}
      >
        {showTableAction && (!isMobileViewport || showActionsMobile) && (
          renderActionButtons()
        )}
      </div>
      {isMobileViewport && (showFilterSection || hasActions) && (
        <div className="table-mobile-toggle-row">
          {showFilterSection && (
            <SecondaryButton
              width="auto"
              label={showFiltersMobile ? "Hide Filters" : "Show Filters"}
              onClickFunction={() => setShowFiltersMobile((prev) => !prev)}
            />
          )}
          {hasActions && (
            isMultiActionModule ? (
              <SecondaryButton
                width="auto"
                label={
                  showActionsMobile ? "Hide Family Actions" : "Family Actions"
                }
                onClickFunction={() => setShowActionsMobile((prev) => !prev)}
              />
            ) : (
              renderActionButtons()
            )
          )}
        </div>
      )}
    </div>
  );
}
